'use client';

import { User } from '@api/models';
import { useTranslations } from 'next-intl';
import React, { useCallback, useState } from 'react';

import { ActivityDrawer } from '@website/components/ActivityDrawer/ActivityDrawer';
import { GoogleMapWrapper } from '@website/components/GoogleMapWrapper';
import { ListingCardsScreenWrapper } from '@website/components/ListingCardsScreenWrapper/ListingCardsScreenWrapper';
import { SEARCH_PARAMS } from '@website/constants/searchParams';
import { useActivities } from '@website/hooks/useActivities';
import { useActivityCardRefs } from '@website/hooks/useActivityCardRefs';
import { useActivitySelection } from '@website/hooks/useActivitySelection';
import { useFilteredActivities } from '@website/hooks/useFilteredActivities';
import { useSearchApiParams } from '@website/hooks/useSearchApiParams';
import { useSyncSearchParams } from '@website/hooks/useSyncSearchParams';
import { extractCity } from '@website/lib/address';
import { type MapBounds } from '@website/utils/mapUtils';

interface ActivitiesListWrapperProps {
  isMapDisplayMode: boolean;
  selectedActivityId: string | undefined;
  setSelectedActivityId: React.Dispatch<React.SetStateAction<string | undefined>>;
  selectionSource: 'map' | 'list' | null;
  setSelectionSource: React.Dispatch<React.SetStateAction<'map' | 'list' | null>>;
  user: User | null;
  mapBounds: MapBounds | null;
  handleMapIdle: (
    bounds: { neLat: number; neLng: number; swLat: number; swLng: number },
    zoom: number | undefined
  ) => void;
  center: { lat: number; lng: number } | null;
  zoom: number;
  panVersion?: number;
  setLocation: (location: { latitude: number; longitude: number; name: string }) => void;
}

export default function ActivitiesListWrapper({
  isMapDisplayMode,
  selectedActivityId,
  setSelectedActivityId,
  selectionSource,
  setSelectionSource,
  user,
  mapBounds,
  handleMapIdle,
  center,
  zoom,
  panVersion,
  setLocation
}: ActivitiesListWrapperProps) {
  const t = useTranslations('ListingCardsScreen');
  const apiParams = useSearchApiParams(mapBounds);
  const isLocationResolving = !mapBounds;
  const { get: getParam } = useSyncSearchParams();

  const {
    data: activitiesResult,
    isLoading: isListActivitiesLoading,
    isFetching: isListActivitiesFetching,
    isError: isListActivitiesError
  } = useActivities(apiParams, {
    enabled: !isLocationResolving
  });

  // Section 1: what matches here - bookable, then not bookable yet (unbadged,
  // muted pins). Section 2: suggestions (bookable, then not bookable yet), listed only (not pinned).
  const listActivities = activitiesResult?.activities;
  const suggestions = activitiesResult?.suggestions;

  const { mapActivities, activitiesForListingCards, mapLocations, atHomeActivities } = useFilteredActivities({
    activities: listActivities,
    zoom
  });

  const {
    hoveredActivityId,
    activityForDrawerData,
    handleLocationSelect,
    handleDrawerClose,
    handleActivityHover: baseHandleActivityHover,
    handleActivityLeaveHover: baseHandleActivityLeaveHover
  } = useActivitySelection({
    activities: mapActivities,
    selectedActivityId,
    setSelectedActivityId,
    selectionSource,
    setSelectionSource
  });

  const { listContainerRef, registerCardRef, scrollCardIntoView, isMobile } = useActivityCardRefs();

  const handleActivityHover = useCallback(
    (activityId: string) => {
      baseHandleActivityHover(activityId);
    },
    [baseHandleActivityHover]
  );

  const handleActivityLeaveHover = useCallback(() => {
    baseHandleActivityLeaveHover();
  }, [baseHandleActivityLeaveHover]);

  // A keyword search is always intersected with the map's location bounds, so
  // a stale or far-away location silently empties otherwise-good keyword
  // results. When that combination produces an empty list, say so explicitly —
  // on mobile the map is hidden, so this text is the only cue. Only when no
  // other filter is active though: with a category/date/venue filter stacked
  // on top, blaming the location would be a guess.
  const searchedLocationName = getParam(SEARCH_PARAMS.DISPLAY_NAME) || '';
  const hasOtherFilters = !!(
    getParam(SEARCH_PARAMS.CATEGORY_ID) ||
    getParam(SEARCH_PARAMS.DATE) ||
    getParam(SEARCH_PARAMS.SLOT_TIME) ||
    getParam(SEARCH_PARAMS.IS_AT_HOME)
  );
  // displayName is a geocoder label ("Paris, Île-de-France, France"): the
  // city is the FIRST segment. extractCity targets street addresses and
  // would name the region here instead.
  const searchedLocationLabel = searchedLocationName.split(',')[0]?.trim() || searchedLocationName;
  const emptyStateText =
    apiParams.query && searchedLocationLabel && !hasOtherFilters
      ? t('noActivitiesFoundNearLocation', {
          query: apiParams.query,
          location: searchedLocationLabel
        })
      : undefined;

  // Nothing matches here but the same search does elsewhere: offer a one-tap
  // jump to the nearest such match - the map pans, the search re-runs there.
  const nearestElsewhere = apiParams.query
    ? suggestions?.find(
        (a) =>
          a.suggestionReason === 'sameSearchElsewhere' &&
          a.availability === 'bookable' &&
          a.location?.coordinates.latitude &&
          a.location?.coordinates.longitude &&
          extractCity(a.location.address)
      )
    : undefined;
  const nearestElsewhereCity = nearestElsewhere ? extractCity(nearestElsewhere.location.address) : undefined;
  const emptyStateAction =
    nearestElsewhere && nearestElsewhereCity ? (
      <button
        type="button"
        className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors"
        onClick={() =>
          setLocation({
            latitude: nearestElsewhere.location.coordinates.latitude,
            longitude: nearestElsewhere.location.coordinates.longitude,
            name: nearestElsewhereCity
          })
        }
      >
        {t('seeResultsNear', { city: nearestElsewhereCity })}
      </button>
    ) : undefined;

  // --- Group navigation for mobile drawer ---
  const [drawerGroupIds, setDrawerGroupIds] = useState<string[]>([]);
  const [drawerGroupIndex, setDrawerGroupIndex] = useState(0);

  const handleMapLocationSelect = useCallback(
    (id: string, source?: 'map' | 'list') => {
      handleLocationSelect(id, source);
      if (source === 'map') {
        scrollCardIntoView(id);

        const atHomeIds = atHomeActivities?.map((a) => a.id) ?? [];
        if (atHomeIds.includes(id)) {
          setDrawerGroupIds(atHomeIds);
          setDrawerGroupIndex(atHomeIds.indexOf(id));
          return;
        }
        for (const loc of mapLocations ?? []) {
          if (loc.activityIds && loc.activityIds.includes(id) && loc.activityIds.length > 1) {
            setDrawerGroupIds(loc.activityIds);
            setDrawerGroupIndex(loc.activityIds.indexOf(id));
            return;
          }
        }
        setDrawerGroupIds([]);
        setDrawerGroupIndex(0);
      }
    },
    [handleLocationSelect, scrollCardIntoView, atHomeActivities, mapLocations]
  );

  const handleDrawerPrev = useCallback(() => {
    if (drawerGroupIds.length === 0) return;
    const newIdx = (drawerGroupIndex - 1 + drawerGroupIds.length) % drawerGroupIds.length;
    setDrawerGroupIndex(newIdx);
    const newId = drawerGroupIds[newIdx]!;
    handleLocationSelect(newId, 'map');
    scrollCardIntoView(newId);
  }, [drawerGroupIds, drawerGroupIndex, handleLocationSelect, scrollCardIntoView]);

  const handleDrawerNext = useCallback(() => {
    if (drawerGroupIds.length === 0) return;
    const newIdx = (drawerGroupIndex + 1) % drawerGroupIds.length;
    setDrawerGroupIndex(newIdx);
    const newId = drawerGroupIds[newIdx]!;
    handleLocationSelect(newId, 'map');
    scrollCardIntoView(newId);
  }, [drawerGroupIds, drawerGroupIndex, handleLocationSelect, scrollCardIntoView]);

  return (
    <div
      className={`flex flex-row flex-1 h-full md:w-full layout-bounds ${isMobile && isMapDisplayMode ? 'mx-0' : ''}`}
    >
      {/* List view - hide on mobile when isMapDisplayMode is true */}
      <div
        className={`flex flex-col w-full md:w-1/2 md:h-[calc(100vh-120px)] ${isMapDisplayMode ? 'hidden md:flex' : ''}`}
      >
        {/* Activity count - fixed above scroll area */}
        {(activitiesForListingCards?.length ?? 0) > 0 && (
          <div className="flex-shrink-0 text-xs font-semibold text-[#1F242F] pt-5 pb-2 md:pl-[5px] md:pr-[14px]">
            {(activitiesForListingCards?.length ?? 0) === 1
              ? `${t('activitiesFoundSingularPrefix')}${activitiesForListingCards?.length ?? 0}${t('activitiesFoundSingularSuffix')}`
              : `${t('activitiesFoundPluralPrefix')}${activitiesForListingCards?.length ?? 0}${t('activitiesFoundPluralSuffix')}`}
          </div>
        )}

        {/* Scrollable list */}
        <div
          ref={listContainerRef}
          className="flex flex-col flex-1 min-h-0 pb-5 md:pl-[5px] md:pr-[14px] md:overflow-y-auto"
        >
          <ListingCardsScreenWrapper
            activities={activitiesForListingCards ?? []}
            totalCount={activitiesForListingCards?.length ?? 0}
            showActivityCount={false}
            isLoading={
              isLocationResolving ||
              ((isListActivitiesLoading || isListActivitiesFetching) && !activitiesForListingCards?.length)
            }
            isError={isListActivitiesError}
            onActivityHover={handleActivityHover}
            onActivityLeaveHover={handleActivityLeaveHover}
            hoveredActivityId={hoveredActivityId ?? undefined}
            selectedActivityId={selectedActivityId}
            registerCardRef={registerCardRef}
            onActivitySelect={(id) => {
              handleLocationSelect(id, 'list');
              scrollCardIntoView(id);
            }}
            user={user}
            badgeUnavailable={false}
            suggestions={suggestions}
            noActivitiesFoundText={emptyStateText}
            emptyStateAction={emptyStateAction}
          />
        </div>
      </div>

      {/* Map view */}
      <div
        className={`
          ${isMapDisplayMode ? 'flex-1 z-30' : 'max-md:absolute max-md:inset-0 max-md:opacity-0 max-md:pointer-events-none max-md:-z-10 md:block z-30'} 
          flex w-full md:w-1/2
        `}
      >
        <GoogleMapWrapper
          noLocation={false}
          defaultCenter={center ?? undefined}
          locations={mapLocations}
          atHomeActivities={atHomeActivities}
          centerLat={center?.lat ?? null}
          centerLng={center?.lng ?? null}
          zoom={zoom}
          panVersion={panVersion}
          className="w-full h-full"
          onLocationSelect={handleMapLocationSelect}
          onMarkerHover={handleActivityHover}
          onMarkerLeaveHover={handleActivityLeaveHover}
          selectedMarkerId={selectedActivityId}
          hoveredMarkerId={hoveredActivityId ?? undefined}
          onBoundsChanged={handleMapIdle}
          isLoading={isLocationResolving || isListActivitiesFetching}
        />
      </div>

      {/* Mobile activity drawer with group navigation */}
      {isMobile && activityForDrawerData && selectionSource === 'map' && (
        <ActivityDrawer
          activity={activityForDrawerData}
          isOpen={!!selectedActivityId}
          onClose={handleDrawerClose}
          onPrev={drawerGroupIds.length > 1 ? handleDrawerPrev : undefined}
          onNext={drawerGroupIds.length > 1 ? handleDrawerNext : undefined}
          currentIndex={drawerGroupIndex}
          totalCount={drawerGroupIds.length > 1 ? drawerGroupIds.length : undefined}
        />
      )}
    </div>
  );
}
